<template>
	<div class="chat-filter-container">
		<FilterDropdown
			v-model="selectedChatIds"
			:options="options"
			:searchPlaceholder="$t('search_chats_filter_placeholder')"
			:filterFn="searchFilterFn"
			optionsClass="search-chat-filter-options"
			@load-more="emit('load-more', $event)"
			@open="onOpen"
			@clear="onClear"
			@search="onSearch"
		>
			<template #placeholder="{ items }">
				<span
					class="chat-placeholder"
					v-if="!items.length"
					v-text="$t('search_chats_filter_title')"
				/>
				<span
					class="chat-placeholder"
					v-if="items.length"
					v-text="
						$t('search_chats_filter_title_with_value', { Chat: items[0].label })
					"
				/>
			</template>

			<template v-slot="{ option }">
				<div class="chat-option-info">
					<div class="chat-option-category" v-if="option.type === 'category'">
						<div class="category-label" v-text="option.label" />
						<div
							class="category-select-all"
							v-if="!showOnlySelected && !allSelectedByCatId[option.id]"
							v-text="$t('announcements_group_select_all')"
							@click.stop="onSelectAll(option.id)"
						/>
					</div>
					<div
						class="chat-option-title group-chat"
						v-if="option.type === 'chat'"
						v-text="option.label"
					/>
					<div class="chat-option-title dm-chat" v-if="option.type === 'dm'">
						<ProfilePic
							v-if="!option.isMultiDM"
							:user="option.user"
							size="26px"
						/>
						<ProfilePizza
							v-if="option.isMultiDM"
							:userIds="option.userIds || []"
							size="26px"
						/>
						<div class="chat-option-dm-name" v-text="option.label" />
					</div>
				</div>
			</template>
		</FilterDropdown>
	</div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import FilterDropdown from './FilterDropdown'
import { ProfilePic, ProfilePizza } from '@/components/common'
import {
	getCategoriesWithChats,
	getChatsWithNoCategory,
	getMembershipsForBubble,
	getUser,
	getUsersForBubble,
} from '@/store'
import { getDMChats } from '@/store/chats'
import bus, { useEventBus } from '@/helpers/bus'
import escapeRegExp from 'escape-string-regexp'
import i18n from '@/i18n'
import debounce from 'debounce'

const model = defineModel({ required: true })
const emit = defineEmits(['open', 'search'])

const allChats = ref(getAllChats())
const selectedChatIds = ref(model.value || [])
const showOnlySelected = ref(selectedChatIds.value.length > 0)
const isOpen = ref(false)
const searchTerm = ref(null)
const displayedOptions = ref(null)
const getAllChatsDebounced = debounce(() => {
	// listen for store updates
	allChats.value = getAllChats()
}, 250)

useEventBus('membership-update', getAllChatsDebounced)

watch(selectedChatIds, (chatIds) => {
	// only update selectedChatIds if different from the model
	const [modelVal, internalVal] = [model.value, chatIds]
		.filter((x) => x)
		.map((ids) => ids.slice().sort().join(','))
	if (modelVal !== internalVal) {
		model.value = chatIds || []
		resetState()
	}
})

watch(model, (value) => {
	// only update the model if different from selectedChatIds
	const [internVal, modelVal] = [selectedChatIds.value, value]
		.filter((x) => x)
		.map((ids) => ids.slice().sort().join(','))
	if (internVal !== modelVal) {
		selectedChatIds.value = value || []
		resetState()
	}
})

const allSelectedByCatId = computed(() => {
	const ids = selectedChatIds.value
	const options = displayedOptions.value || allChats.value
	// TODO: clean this up because this nested looping sucks
	return options
		.filter((c) => c.type === 'category')
		.reduce((acc, cat) => {
			acc[cat.id] = options
				.filter((c) => c.categoryId === cat.id)
				.map((chat) => chat.id)
				.every((id) => ids.includes(id))
			return acc
		}, {})
})

const options = computed(() => {
	if (searchTerm.value) return allChats.value
	if (selectedChatIds.value.length > 0) {
		const selectedChats = selectedChatIds.value.map((id) =>
			allChats.value.find((c) => c.id === id)
		)
		const categoryIds = selectedChats.map((c) => c.categoryId)
		const idsToShow = selectedChatIds.value.concat(categoryIds)
		const chats = allChats.value.filter((c) => idsToShow.includes(c.id))
		return chats
	}
	return allChats.value
})

function resetState() {
	showOnlySelected.value = selectedChatIds.value.length > 0
	searchTerm.value = null
	displayedOptions.value = null
	bus.$emit('clear-dropdown-search')
}

function onSelectAll(catId) {
	const options = displayedOptions.value || allChats.value
	const chatsForCategory = options.filter((c) => c.categoryId === catId)
	const chatIds = chatsForCategory.map((c) => c.id)
	selectedChatIds.value = selectedChatIds.value.concat(chatIds)
	nextTick(() => {
		showOnlySelected.value = true
	})
}

function onOpen(open) {
	emit('open', open)
	isOpen.value = open
	if (!open) {
		showOnlySelected.value = selectedChatIds.value.length > 0
	}
}

function onSearch(term) {
	emit('search', term)
	searchTerm.value = term || null
	if (searchTerm.value) {
		showOnlySelected.value = false
		const regex = new RegExp(escapeRegExp(term), 'i')
		displayedOptions.value = allChats.value.filter((chat) =>
			searchFilterFn(chat, regex)
		)
	} else {
		displayedOptions.value = null
	}
}

function onClear() {
	showOnlySelected.value = false
}

function searchFilterFn(chat, regex) {
	if (chat.chatLabels)
		return regex.test(chat.chatLabels) || regex.test(chat.label)
	return regex.test(chat.label) || regex.test(chat.categoryLabel)
}

function getAllChats() {
	// TODO: clean this up because this whole function is 5x longer than it needs to be
	let chats = getCategoriesWithChats().flatMap(({ id, displayname, chats }) => [
		{
			id,
			label: displayname,
			type: 'category',
			noHover: true,
			chatLabels: chats.map((c) => c.displayname).join('|'),
		},
		...chats.map((chat) => ({
			id: chat.id,
			label: chat.displayname,
			categoryId: id,
			categoryLabel: displayname,
			type: 'chat',
		})),
	])
	const uncategorized = getChatsWithNoCategory()
	if (uncategorized.length) {
		const label = i18n.global.t('chat_drawer_section_bubbles_uncatagorized')
		chats = chats.concat([
			{
				id: 'none',
				label,
				type: 'category',
				noHover: true,
				chatLabels: uncategorized.map((c) => c.displayname).join(' | '),
			},
			...uncategorized.map((chat) => ({
				id: chat.id,
				label: chat.displayname,
				categoryId: chat.category_id,
				categoryLabel: label,
				type: 'chat',
			})),
		])
	}
	const dms = getDMChats()
	if (dms.length) {
		const label = i18n.global.t('chat_drawer_section_directmessages')
		chats = chats.concat([
			{
				id: 'dms',
				label,
				type: 'category',
				noHover: true,
				chatLabels: dms.map((c) => c.displayname).join(' | '),
			},
			...dms
				.map((chat) => ({
					type: 'dm',
					id: chat.id,
					label: chat.displayname,
					categoryId: 'dms',
					categoryLabel: label,
					isMultiDM: chat.isMultiDM,
					user: chat.isMultiDM ? null : getUser(chat.dmpartner_id),
					userIds: chat.isMultiDM
						? getUsersForBubble(chat.id).map((m) => m.id)
						: [],
				}))
				.sort((a, b) => a.label.localeCompare(b.label)),
		])
	}
	return chats
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.chat-option-info {
	width: 100%;
	.chat-option-category {
		@include flex-spread(8px);
		color: var(--neutral-600);
		background: var(--neutral-100);
		padding: 4px 16px;
		margin: 4px 0;
		font-size: 12px;
		.category-label {
			@include ellipsis;
			text-transform: uppercase;
			letter-spacing: 1.4px;
		}
		.category-select-all {
			cursor: pointer;
			color: var(--blue-300);
			flex-shrink: 0;
		}
	}
	.chat-option-title {
		@include flex(8px);
		max-width: 100%;
		.profile-pizza {
			flex-shrink: 0;
		}
		.chat-option-dm-name {
			flex-grow: 1;
			@include ellipsis;
		}
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

.search-chat-filter-options {
	.dropdown-option {
		width: calc(100% - 8px) !important;
		margin: 4px !important;
		&:first-child {
			&.no-hover {
				margin: 0 0 4px 0 !important;
				.chat-option-category {
					margin: 0 0 4px 0 !important;
				}
			}
		}
		&.no-hover {
			height: 24px !important;
			width: 100% !important;
			margin: 4px 0 !important;
		}
	}
}

@include overrideScheme('dark') {
	.chat-filter-container {
		.dropdown-container {
			.dropdown-selected {
				.search-filter-placeholder {
					color: white;
				}
			}
		}
		.search-filter-container.active {
			.dropdown-selected {
				.pronto-icon.chevron {
					color: white;
				}
			}
		}
	}
	.search-chat-filter-options {
		background: var(--neutral-200) !important;
		.dropdown-search {
			background: var(--neutral-200) !important;
			.pronto-input {
				background-color: unset !important;
			}
		}
	}
}
</style>
